import * as i0 from '@angular/core';
import { HostListener, HostBinding, Input, Directive, EventEmitter, ContentChildren, Output, Component, Injectable, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FocusKeyManager, CdkTrapFocus, A11yModule } from '@angular/cdk/a11y';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import * as i1 from '@angular/cdk/overlay';
import { OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';

/**
 * To use this component in a module, you can import the `WindmillContextMenuModule`:
 * @example
 * ```typescript
 * import { WindmillContextMenuModule } from '@windmill/ng-windmill/context-menu';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillContextMenuItemDirective`:
 * @example
 * ```typescript
 * import { WindmillContextMenuItemDirective } from '@windmill/ng-windmill/context-menu';
 * ```
 */
class WindmillContextMenuItemDirective {
    elementRef;
    /**
     * Sets the aria-current attribute on the item and styles it to look like it has been selected
     */
    set isSelected(value) {
        this._isSelected = value;
        const hostElement = this.elementRef.nativeElement;
        if (this._isSelected) {
            hostElement?.setAttribute('aria-current', 'true');
        }
        else {
            hostElement?.removeAttribute('aria-current');
        }
    }
    get isSelected() {
        return this._isSelected;
    }
    /**
     * Sets the aria-disabled attribute on the item and styles it to look like it has been disabled.
     * If the element allows it, apply the HTML disabled attribute instead.
     */
    set isDisabled(value) {
        this._isDisabled = value;
        const hostElement = this.elementRef.nativeElement;
        if (this._isDisabled) {
            hostElement?.setAttribute('aria-disabled', 'true');
        }
        else {
            hostElement?.removeAttribute('aria-disabled');
        }
    }
    get isDisabled() {
        return this._isDisabled;
    }
    /**
     * Adds a top border on the item
     */
    isFooter = false;
    /**
     * Sets the role attribute on the host element
     */
    role = 'menuitem';
    _tabindex = '-1';
    _isDisabled = false;
    _isSelected = false;
    constructor(elementRef) {
        this.elementRef = elementRef;
    }
    get tabindex() {
        return this._tabindex;
    }
    set tabindex(value) {
        this._tabindex = value;
    }
    updateTabindex(event) {
        if (event.type === 'focusin') {
            this.tabindex = this.disabled ? '-1' : '0';
        }
        else {
            this.tabindex = '-1';
        }
    }
    get disabled() {
        const hasDisabledAttribute = this.elementRef.nativeElement?.hasAttribute('disabled');
        return this.isDisabled || hasDisabledAttribute;
    }
    focus() {
        this.elementRef.nativeElement.focus();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuItemDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillContextMenuItemDirective, isStandalone: true, selector: "[windmill-context-menu-item]", inputs: { isSelected: "isSelected", isDisabled: "isDisabled", isFooter: "isFooter", role: "role" }, host: { listeners: { "focusout": "updateTabindex($event)", "focusin": "updateTabindex($event)" }, properties: { "class.is-selected": "this.isSelected", "class.is-disabled": "this.isDisabled", "class.footer-content": "this.isFooter", "attr.role": "this.role", "attr.tabindex": "this.tabindex" } }, exportAs: ["windmill-context-menu-item"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmill-context-menu-item]',
                    exportAs: 'windmill-context-menu-item',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { isSelected: [{
                type: HostBinding,
                args: ['class.is-selected']
            }, {
                type: Input
            }], isDisabled: [{
                type: HostBinding,
                args: ['class.is-disabled']
            }, {
                type: Input
            }], isFooter: [{
                type: HostBinding,
                args: ['class.footer-content']
            }, {
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }, {
                type: Input
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], updateTabindex: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }, {
                type: HostListener,
                args: ['focusin', ['$event']]
            }] } });

const DEFAULT_LIST_MAX_HEIGHT = 256;
/**
 * To use this component in a module, you can import the `WindmillContextMenuModule`:
 * @example
 * ```typescript
 * import { WindmillContextMenuModule } from '@windmill/ng-windmill/context-menu';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillContextMenuListComponent`:
 * @example
 * ```typescript
 * import { WindmillContextMenuListComponent } from '@windmill/ng-windmill/context-menu';
 * ```
 */
class WindmillContextMenuListComponent {
    /**
     * Sets the id attribute on the list
     */
    listId;
    /**
     * Sets the data-testid attribute on the list
     */
    listDataTestId;
    /**
     * Sets the max-height of the list
     */
    listMaxHeight = DEFAULT_LIST_MAX_HEIGHT;
    /**
     * Emits a boolean when the list overlay is opened or closed
     */
    openedListChange = new EventEmitter(false);
    items;
    keyManager;
    onKeyDown(event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            this.keyManager?.onKeydown(event);
        }
    }
    ngAfterContentInit() {
        this._initializeKeyManager();
    }
    ngAfterViewInit() {
        this.openedListChange.emit(true);
    }
    ngOnDestroy() {
        this.openedListChange.emit(false);
    }
    _initializeKeyManager() {
        this.keyManager = new FocusKeyManager(this.items)
            .skipPredicate((item) => item.disabled)
            .withWrap();
        const selectedItem = this.items.find(item => item.isSelected);
        if (selectedItem) {
            this.keyManager.setActiveItem(selectedItem);
        }
        else {
            this.keyManager.setFirstItemActive();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.10", type: WindmillContextMenuListComponent, isStandalone: true, selector: "windmill-context-menu-list", inputs: { listId: "listId", listDataTestId: "listDataTestId", listMaxHeight: "listMaxHeight" }, outputs: { openedListChange: "openedListChange" }, host: { listeners: { "keydown": "onKeyDown($event)" } }, queries: [{ propertyName: "items", predicate: WindmillContextMenuItemDirective, descendants: true }], ngImport: i0, template: "<section class=\"windmill-context-menu height-{{ listMaxHeight }}\">\r\n  <div\r\n    cdkTrapFocus\r\n    class=\"menu-content\"\r\n    role=\"menu\"\r\n    [attr.id]=\"listId\"\r\n    [attr.data-testid]=\"listDataTestId\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</section>\r\n", dependencies: [{ kind: "directive", type: CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'windmill-context-menu-list', imports: [CdkTrapFocus], standalone: true, template: "<section class=\"windmill-context-menu height-{{ listMaxHeight }}\">\r\n  <div\r\n    cdkTrapFocus\r\n    class=\"menu-content\"\r\n    role=\"menu\"\r\n    [attr.id]=\"listId\"\r\n    [attr.data-testid]=\"listDataTestId\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</section>\r\n" }]
        }], propDecorators: { listId: [{
                type: Input
            }], listDataTestId: [{
                type: Input
            }], listMaxHeight: [{
                type: Input
            }], openedListChange: [{
                type: Output
            }], items: [{
                type: ContentChildren,
                args: [WindmillContextMenuItemDirective, { descendants: true }]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const DEFAULT_CONNECTED_POSITIONS = [
    {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 8
    },
    {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetY: -8
    },
    {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8
    },
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -8
    }
];

function getOverlayConfig(overlay, elementRef, maxHeight) {
    return {
        hasBackdrop: true,
        backdropClass: 'transparent-backdrop',
        panelClass: maxHeight ? `height-${maxHeight}` : '',
        scrollStrategy: overlay.scrollStrategies.reposition(),
        positionStrategy: overlay
            .position()
            .flexibleConnectedTo(elementRef)
            .withPositions(DEFAULT_CONNECTED_POSITIONS)
            .withFlexibleDimensions(false)
            .withPush(false)
    };
}

const DEFAULT_MAX_HEIGHT = 256;
/**
 * import { WindmillContextMenuModule } from '@windmill/ng-windmill/context-menu';
 */
class WindmillContextMenuService {
    overlay;
    overlayRef;
    overlayMaxHeight = DEFAULT_MAX_HEIGHT;
    constructor(overlay) {
        this.overlay = overlay;
    }
    openList(component, elementRef, overlayMaxHeight) {
        this.overlayMaxHeight = overlayMaxHeight ? overlayMaxHeight : DEFAULT_MAX_HEIGHT;
        this.overlayRef = this.createOverlay(elementRef);
        return this.attachList(this.overlayRef, component);
    }
    closeList() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }
    attachList(overlayRef, component) {
        const contextMenuPortal = new ComponentPortal(component);
        this.overlayRef.overlayElement.focus();
        return overlayRef.attach(contextMenuPortal);
    }
    createOverlay(elementRef) {
        const overlayConfig = getOverlayConfig(this.overlay, elementRef, this.overlayMaxHeight);
        return this.overlay.create(new OverlayConfig(overlayConfig));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuService, deps: [{ token: i1.Overlay }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.Overlay }] });

const FOCUSABLE_ELEMENTS_QUERY = 'button, [tabindex="0"]';
/**
 * To use this component in a module, you can import the `WindmillContextMenuModule`:
 * @example
 * ```typescript
 * import { WindmillContextMenuModule } from '@windmill/ng-windmill/context-menu';
 * ```
 *
 * To use the standalone component directly, you can import `WindmillContextMenuDirective`:
 * @example
 * ```typescript
 * import { WindmillContextMenuDirective } from '@windmill/ng-windmill/context-menu';
 * ```
 */
class WindmillContextMenuDirective {
    _viewContainerRef;
    _overlay;
    _elementRef;
    /**
     * Property for the list template
     */
    get windmillContextMenu() {
        return this._menu;
    }
    set windmillContextMenu(value) {
        this._menu = value;
    }
    _menu;
    _overlayRef = null;
    _backdropClickSubscription = Subscription.EMPTY;
    _keydownEventsSubscription = Subscription.EMPTY;
    get _hasTabIndexAttribute() {
        return this._elementRef.nativeElement.getAttribute('tabindex') === '0';
    }
    constructor(_viewContainerRef, _overlay, _elementRef) {
        this._viewContainerRef = _viewContainerRef;
        this._overlay = _overlay;
        this._elementRef = _elementRef;
    }
    onClick() {
        this.createOverlay();
    }
    onKeydown(event) {
        if (this._hasTabIndexAttribute && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            this.createOverlay();
        }
    }
    ngOnDestroy() {
        this._backdropClickSubscription?.unsubscribe();
        this._keydownEventsSubscription?.unsubscribe();
    }
    closeOverlay(isClosedViaKeyboard) {
        this._overlayRef?.dispose();
        if (isClosedViaKeyboard) {
            setTimeout(() => {
                this._focusTriggerElement();
            });
        }
    }
    createOverlay() {
        const overlayConfig = getOverlayConfig(this._overlay, this._elementRef);
        this._overlayRef = this._overlay.create(overlayConfig);
        this._openOverlay();
        this._subscribeToOverlayEvents();
    }
    _getPortal() {
        return new TemplatePortal(this._menu, this._viewContainerRef);
    }
    _subscribeToOverlayEvents() {
        this._backdropClickSubscription = this._overlayRef?.backdropClick().subscribe(() => {
            this.closeOverlay();
        });
        this._keydownEventsSubscription = this._overlayRef?._keydownEvents.subscribe((event) => {
            if (event.key === 'Escape' || event.key === 'Tab') {
                event.preventDefault();
                this.closeOverlay();
                this._focusTriggerElement();
            }
        });
    }
    _openOverlay() {
        this._overlayRef?.attach(this._getPortal());
    }
    _focusTriggerElement() {
        const isButton = this._elementRef.nativeElement.tagName === 'BUTTON';
        const focusableTrigger = this._elementRef.nativeElement.querySelector(FOCUSABLE_ELEMENTS_QUERY);
        if (isButton || this._hasTabIndexAttribute) {
            this._elementRef.nativeElement.focus();
        }
        else if (focusableTrigger) {
            focusableTrigger.focus();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuDirective, deps: [{ token: i0.ViewContainerRef }, { token: i1.Overlay }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: WindmillContextMenuDirective, isStandalone: true, selector: "[windmill-context-menu]", inputs: { windmillContextMenu: ["windmill-context-menu", "windmillContextMenu"] }, host: { listeners: { "click": "onClick($event)", "keydown": "onKeydown($event)" } }, providers: [WindmillContextMenuService], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[windmill-context-menu]',
                    providers: [WindmillContextMenuService],
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: i1.Overlay }, { type: i0.ElementRef }], propDecorators: { windmillContextMenu: [{
                type: Input,
                args: ['windmill-context-menu']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const moduleComponents = [WindmillContextMenuDirective, WindmillContextMenuItemDirective, WindmillContextMenuListComponent];
class WindmillContextMenuModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuModule, imports: [A11yModule, CommonModule, OverlayModule, WindmillContextMenuDirective, WindmillContextMenuItemDirective, WindmillContextMenuListComponent], exports: [WindmillContextMenuDirective, WindmillContextMenuItemDirective, WindmillContextMenuListComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuModule, providers: [WindmillContextMenuService], imports: [A11yModule, CommonModule, OverlayModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: WindmillContextMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [A11yModule, CommonModule, OverlayModule, ...moduleComponents],
                    exports: [...moduleComponents],
                    providers: [WindmillContextMenuService],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { WindmillContextMenuDirective, WindmillContextMenuItemDirective, WindmillContextMenuListComponent, WindmillContextMenuModule, WindmillContextMenuService };
